import { z } from 'zod';
import { decimalInRange, decimalStringSchema } from './common';

/**
 * Risk settings (`risk_settings`, single row id = 1). Percentages are decimal strings so
 * the server can compare them with `Decimal.gte` instead of float operators (AI_RULES §5).
 */

/** A percentage in "0.000".."100.000" form, as a string. */
export const percentSchema = decimalStringSchema.refine(
  decimalInRange('0', '100'),
  'must be between 0 and 100',
);

/** A 0..1 ratio in string form. */
export const ratioSchema = decimalStringSchema.refine(
  decimalInRange('0', '1'),
  'must be between 0 and 1',
);

export const riskSettingsSchema = z.object({
  maxConcurrentTrades: z.number().int().min(1).max(50),
  maxDailyDrawdownPct: percentSchema,
  basePositionPct: percentSchema,
  maxPositionPct: percentSchema,
  minConfidence: ratioSchema,
  cooldownAfterLossMin: z.number().int().min(0).max(1440),
});
export type RiskSettings = z.infer<typeof riskSettingsSchema>;

export const riskSettingsResponseSchema = riskSettingsSchema.extend({
  circuitBreakerTripped: z.boolean(),
  circuitBreakerTrippedAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime(),
});
export type RiskSettingsResponse = z.infer<typeof riskSettingsResponseSchema>;

/** PUT /risk accepts any subset; the server merges onto the current row. */
export const riskSettingsUpdateSchema = riskSettingsSchema.partial().refine(
  (v) => Object.keys(v).length > 0,
  'at least one field must be provided',
);
export type RiskSettingsUpdate = z.infer<typeof riskSettingsUpdateSchema>;
